import { motion, AnimatePresence } from "framer-motion";
import { X, GitCompare, Trash2 } from "lucide-react";
import { formatMoney } from "../../utils/formatMoney";

export default function ComparisonBar({ 
  selectedPolicies = [], 
  onRemove, 
  onCompare, 
  onClear,
  maxSelection = 2 
}) {
  if (selectedPolicies.length === 0) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed right-0 bottom-0 left-0 z-50 px-4 pb-4 pointer-events-none sm:px-6 lg:px-8"
      >
        <div className="mx-auto max-w-7xl">
          <div className="overflow-hidden bg-white rounded-2xl border-2 border-emerald-500 shadow-2xl pointer-events-auto">
            {/* Header */}
            <div className="flex justify-between items-center px-4 py-3 bg-gradient-to-r from-emerald-500 to-green-600 sm:px-6">
              <div className="flex gap-2 items-center">
                <div className="flex justify-center items-center w-8 h-8 bg-white rounded-lg">
                  <GitCompare className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white sm:text-base">
                    Compare Policies
                  </h3>
                  <p className="text-xs text-emerald-50">
                    {selectedPolicies.length} of {maxSelection} selected
                  </p>
                </div>
              </div>
              <button
                onClick={onClear}
                className="p-2 text-white rounded-lg transition-colors hover:bg-white/20"
                aria-label="Clear all selections"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            {/* Selected Policies */}
            <div className="p-4 bg-gray-50 sm:p-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {selectedPolicies.map((policy) => (
                  <motion.div
                    key={policy.id}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    className="relative p-3 bg-white rounded-lg border border-gray-200 shadow-sm"
                  >
                    <button
                      onClick={() => onRemove(policy.id)}
                      className="flex absolute -top-2 -right-2 z-10 justify-center items-center w-6 h-6 text-white bg-red-500 rounded-full shadow-md transition-all hover:bg-red-600 hover:scale-110"
                      aria-label="Remove policy"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                    <div className="pr-3 mb-2">
                      <h4 className="text-sm font-semibold leading-tight text-gray-900 line-clamp-2">
                        {policy.policyName}
                      </h4>
                      <p className="mt-1 text-xs text-gray-600 line-clamp-1">
                        {policy.provider}
                      </p>
                    </div>
                    <div className="flex justify-between items-end pt-2 border-t border-gray-100">
                      <span className="text-[10px] text-gray-500">From</span>
                      <span className="text-base font-bold text-emerald-600">
                        {formatMoney(policy.premiumMin)}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Compare Button */}
              <motion.button
                onClick={onCompare}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full mt-5 px-6 py-3.5 text-base font-semibold rounded-xl transition-all shadow-lg text-white bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700"
              >
                <span className="flex gap-2 justify-center items-center">
                  <GitCompare className="w-5 h-5" />
                  Compare Selected Policies
                </span>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
